import type { RemoteCommand, RemoteKey } from './remoteProtocol'

const SHORTCUTS: Partial<Record<RemoteKey, RemoteCommand>> = {
  home: { kind: 'navigate', section: 'home' },
  menu: { kind: 'navigate', section: 'settings' },
  red: { kind: 'navigate', section: 'home' },
  green: { kind: 'navigate', section: 'search' },
  yellow: { kind: 'navigate', section: 'library' },
  blue: { kind: 'navigate', section: 'settings' },
  play: { kind: 'player', action: { kind: 'play' } },
  pause: { kind: 'player', action: { kind: 'pause' } },
  stop: { kind: 'player', action: { kind: 'stop' } },
  rewind: { kind: 'player', action: { kind: 'seek_by', seconds: -30 } },
  fast_forward: { kind: 'player', action: { kind: 'seek_by', seconds: 30 } },
}

export interface RemoteKeyEvent {
  key: string
  code: string
  keyCode: number
}

const KEY_EVENTS: Partial<Record<RemoteKey, RemoteKeyEvent>> = {
  up: { key: 'ArrowUp', code: 'ArrowUp', keyCode: 38 },
  down: { key: 'ArrowDown', code: 'ArrowDown', keyCode: 40 },
  left: { key: 'ArrowLeft', code: 'ArrowLeft', keyCode: 37 },
  right: { key: 'ArrowRight', code: 'ArrowRight', keyCode: 39 },
  ok: { key: 'Enter', code: 'Enter', keyCode: 13 },
  back: { key: 'Escape', code: 'Escape', keyCode: 461 },
  info: { key: 'Info', code: 'Info', keyCode: 457 },
}

/** LG webOS hardware key codes. These are handled globally so color/media
 * shortcuts work on the physical Magic Remote, not only on the phone clone. */
const LG_HARDWARE_KEYS: Partial<Record<number, RemoteKey>> = {
  19: 'pause',
  403: 'red',
  404: 'green',
  405: 'yellow',
  406: 'blue',
  412: 'rewind',
  413: 'stop',
  415: 'play',
  417: 'fast_forward',
  457: 'info',
}

export function nativeRemoteCommand(keyCode: number): RemoteCommand | null {
  const key = LG_HARDWARE_KEYS[keyCode]
  return key ? normalizeRemoteCommand({ kind: 'key', key }) : null
}

export function normalizeRemoteCommand(command: RemoteCommand): RemoteCommand {
  if (command.kind !== 'key') return command
  return SHORTCUTS[command.key] || command
}

export function remoteKeyEvent(key: RemoteKey) {
  return KEY_EVENTS[key] || null
}

export function dispatchRemoteKey(key: RemoteKey) {
  const event = remoteKeyEvent(key)
  if (!event) return false
  if (key === 'back') {
    window.dispatchEvent(new CustomEvent('vela:back'))
    return true
  }
  const keyboardEvent = new KeyboardEvent('keydown', {
    key: event.key,
    code: event.code,
    bubbles: true,
  })
  // Chrome 79 keeps keyCode readonly and does not infer it for synthetic keys.
  Object.defineProperty(keyboardEvent, 'keyCode', { value: event.keyCode })
  Object.defineProperty(keyboardEvent, 'which', { value: event.keyCode })
  window.dispatchEvent(keyboardEvent)
  return true
}
